/** @module Components */
import React from 'react'
import * as Yup from 'yup'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { SubscriberFormType } from '../../types'
import { usePostSubscriberMutation } from '../../redux/services/subscriberService'

/** @category Prop Types */
export type SubscriberFormProps = {
  /* Whether to include the center application field and change the button text */
  centerApplicationRequest: boolean
}

export const SubscriberForm: React.FC<SubscriberFormProps> = ({
  centerApplicationRequest
}: SubscriberFormProps) => {
  const navigate = useNavigate()
  const [postSubscriberForm] = usePostSubscriberMutation()

  const formValidationSchema = Yup.object({
    firstName: Yup.string()
      .min(1, 'Too Short!')
      .max(75, 'Too Long!')
      .required('Required'),
    lastName: Yup.string()
      .min(1, 'Too Short!')
      .max(75, 'Too Long!')
      .required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    centerName: centerApplicationRequest
      ? Yup.string().required('Required')
      : Yup.string()
  })

  const handleSubmitSuccess = () => {
    navigate('/', { replace: false })
    toast.success(
      'Your submission has been received and will be processed as soon as possible. Thank you!'
    )
  }

  const handleSubmitError = (error: unknown) => {
    if (
      error &&
      typeof error === 'object' &&
      'status' in error &&
      error.status === 409
    ) {
      toast.error(
        'There was an issue submitting your form. That email has been used to sign up already. Please reach out to support@sicklecellcenters.org for help.'
      )
    } else {
      toast.error(
        'There was an issue submitting your form. Please reach out to support@sicklecellcenters.org for help.'
      )
    }
  }

  return (
    <div className="w-full max-w-2xl mx-auto px-4">
      <div className="bg-white rounded-lg shadow-md border border-[#9298a8]/20 p-6 md:p-8">
        <Formik
          initialValues={{
            firstName: '',
            lastName: '',
            email: '',
            centerName: ''
          }}
          validationSchema={formValidationSchema}
          onSubmit={async (values: SubscriberFormType) => {
            try {
              await postSubscriberForm(values).unwrap()
              handleSubmitSuccess()
            } catch (error) {
              handleSubmitError(error)
            }
          }}
        >
          {({ handleSubmit, isValid, dirty }) => (
            <Form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="first-name"
                    className="block text-sm font-medium text-[#3C2416] mb-1"
                  >
                    First Name
                  </label>
                  <Field
                    id="first-name"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    aria-label="First Name"
                    className="w-full px-3 py-2 rounded bg-white text-[#3C2416] text-sm border border-[#9298a8] focus:outline-none focus:ring-2 focus:ring-[#A6410A] focus:border-transparent"
                    placeholder="Enter your first name"
                  />
                  <ErrorMessage
                    name="firstName"
                    component="div"
                    className="mt-1 text-sm text-[#A6410A]"
                  />
                </div>
                <div>
                  <label
                    htmlFor="last-name"
                    className="block text-sm font-medium text-[#3C2416] mb-1"
                  >
                    Last Name
                  </label>
                  <Field
                    id="last-name"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    aria-label="Last Name"
                    className="w-full px-3 py-2 rounded bg-white text-[#3C2416] text-sm border border-[#9298a8] focus:outline-none focus:ring-2 focus:ring-[#A6410A] focus:border-transparent"
                    placeholder="Enter your last name"
                  />
                  <ErrorMessage
                    name="lastName"
                    component="div"
                    className="mt-1 text-sm text-[#A6410A]"
                  />
                </div>
              </div>

              {centerApplicationRequest && (
                <div>
                  <label
                    htmlFor="center-name"
                    className="block text-sm font-medium text-[#3C2416] mb-1"
                  >
                    Center Name
                  </label>
                  <Field
                    id="center-name"
                    name="centerName"
                    type="text"
                    autoComplete="organization"
                    required
                    aria-label="Center Name"
                    className="w-full px-3 py-2 rounded bg-white text-[#3C2416] text-sm border border-[#9298a8] focus:outline-none focus:ring-2 focus:ring-[#A6410A] focus:border-transparent"
                    placeholder="Enter the name of your center"
                  />
                  <ErrorMessage
                    name="centerName"
                    component="div"
                    className="mt-1 text-sm text-[#A6410A]"
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-[#3C2416] mb-1"
                >
                  Email Address
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  aria-label="Email Address"
                  className="w-full px-3 py-2 rounded bg-white text-[#3C2416] text-sm border border-[#9298a8] focus:outline-none focus:ring-2 focus:ring-[#A6410A] focus:border-transparent"
                  placeholder="Enter your email address"
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="mt-1 text-sm text-[#A6410A]"
                />
              </div>

              <div className="flex justify-end mt-6">
                <button
                  type="submit"
                  className="px-6 py-2 rounded-full bg-[#A6410A] text-white text-sm font-medium hover:bg-[#894d35] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!(dirty && isValid)}
                >
                  {centerApplicationRequest
                    ? 'Request an Application'
                    : 'Sign Up'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
        <p className="mt-6 text-sm text-[#142841]">
          We care about the protection of your data. Read our{' '}
          <Link
            to="/privacy-policy"
            className="text-[#A6410A] hover:text-[#894d35] underline"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  )
}
